import { createSelector } from '@reduxjs/toolkit';
import {
    selectErrno,
    selectBetsErrors,
    selectBets,
} from '~Betslip/selectors/betReceiptSelectors';

import {
    selectSelectionDetails,
} from '~Betslip/selectors/selectionDetailsSelectors';

import {
    getPopupErrors,
} from '~Betslip/utils/errorMappings';

import {
    getMapSelectionIdToCombinations,
    getSelectionSuspendedSingle,
} from '~shared/utils/betslip-utils';

import formatCurrency from '~shared/utils/currencyUtils';

const getSuspendedModalDetails = (error, combinationsMap, allSelections) => {
    const { id: errorCode } = error;
    const isMultibet = errorCode === 'SELECTION_SUSPENDED_MULTIBET';

    const suspendedSelections = isMultibet
        ? error
        : getSelectionSuspendedSingle(error, combinationsMap);

    const selections = Object
        .keys(suspendedSelections?.data?.selections || {})
        .map(
            suspendedId => ({
                ...allSelections[suspendedId],
                id: suspendedId,
            }),
        );

    return ({
        type: 'suspended',
        title: 'betslip.selection_suspended',
        selections,
        isMultibet,
    });
};


const getGeneralModalDetails = () => {
    return {
        type: 'general',
        title: 'betslip.error',
    };
};

export const selectModalDetails = createSelector(
    [
        selectErrno,
        selectBetsErrors,
        selectBets,
        selectSelectionDetails,
    ],
    (errno, fullListOfErrors, bets, selectionDetails) => {

        if (errno === 500) {
            return getGeneralModalDetails();
        }

        // Special case for betslip selection limit
        // this is triggered on adding to betslip not placement
        if (errno === 'BETSLIP_LIMIT') {
            return {
                type: 'betslip_limit',
                title: 'betslip.betslip_limit',
                content: 'betslip.betslip_limit_reached',
            };
        }

        const mapSelectionIdToCombinations = getMapSelectionIdToCombinations(bets);
        let selectionSuspended = false;
        let priceChanged = false;
        let basicModal = false;
        let type = null;
        let title = null;
        let content = null;
        let translationVariables = null;
        let button = null;
        let icon = null;

        const errors = getPopupErrors(fullListOfErrors);

        if (errors?.length) {
            errors.forEach((error) => {
                const {
                    id: errorCode,
                    data,
                } = error;

                const amount = formatCurrency(data?.remaining_limit);

                switch (errorCode) {
                    case 'SELECTION_SUSPENDED_SINGLE':
                    case 'SELECTION_SUSPENDED_MULTIBET':
                        selectionSuspended = getSuspendedModalDetails(
                            error,
                            mapSelectionIdToCombinations,
                            selectionDetails,
                        );
                        break;
                    case 'PRICE_CHANGED':
                        priceChanged = true;
                        break;
                    case 'INSUFFICIENT_FUNDS':
                        basicModal = true;
                        type = 'insufficient_funds';
                        title = 'betslip.insufficient_funds';
                        content = 'betslip.no_enough_funds_to_bet';
                        break;
                    case 'REACHED_DAILY_WAGER_LIMIT':
                        basicModal = true;
                        type = 'bet_limits';
                        title = 'betslip.bet_limit_reached';
                        content = 'betslip.bet_limit_text_daily';
                        translationVariables = { amount };
                        break;
                    case 'REACHED_WEEKLY_WAGER_LIMIT':
                        basicModal = true;
                        type = 'bet_limits';
                        title = 'betslip.bet_limit_reached';
                        content = 'betslip.bet_limit_text_weekly';
                        translationVariables = { amount };
                        break;
                    case 'REACHED_MONTHLY_WAGER_LIMIT':
                        basicModal = true;
                        type = 'bet_limits';
                        title = 'betslip.bet_limit_reached';
                        content = 'betslip.bet_limit_text_monthly';
                        translationVariables = { amount };
                        break;
                    case 'REACHED_DAILY_LOSS_LIMIT':
                        basicModal = true;
                        type = 'loss_limits';
                        title = 'betslip.loss_limit_reached';
                        content = 'betslip.loss_limit_text_daily';
                        translationVariables = { amount };
                        break;
                    case 'REACHED_WEEKLY_LOSS_LIMIT':
                        basicModal = true;
                        type = 'loss_limits';
                        title = 'betslip.loss_limit_reached';
                        content = 'betslip.loss_limit_text_weekly';
                        translationVariables = { amount };
                        break;
                    case 'REACHED_MONTHLY_LOSS_LIMIT':
                        basicModal = true;
                        type = 'loss_limits';
                        title = 'betslip.loss_limit_reached';
                        content = 'betslip.loss_limit_text_monthly';
                        translationVariables = { amount };
                        break;
                    case 'CUSTOMER_EXCEEDED_SPAIN_INPLAY_WAGERING_LIMIT':
                        basicModal = true;
                        type = 'spain_inplay_wagering';
                        title = 'betslip.live_betting_limit_title';
                        content = 'betslip.live_betting_limit_event';
                        translationVariables = {
                            event: data?.event_name,
                            limit: formatCurrency(Number(data?.exceeded_live_betting_amount) / 100),
                        };
                        break;
                    case 'GEO_EVENT_ERROR':
                        basicModal = true;
                        type = 'geo_event_error';
                        title = 'betslip.geo_locate_auth_event_fail_title';
                        content = 'betslip.geo_locate_auth_event_fail_text';
                    break;
                    case 'TIME_LIMIT_REACHED':
                        basicModal = true;
                        type = 'time_limit_reached';
                        title = 'betslip.daily_limit';
                        content = 'betslip.bet_not_placed';
                        break;
                    case 'ERROR_CODE_PLAYER_NOT_AUTHENTICATED_DUE_TO_LUGAS_PARALLEL_PLAYING':
                        basicModal = true;
                        type = 'player_not_authenticated_due_to_lugas_parallel_playing';
                        title = 'betslip.notification';
                        content = 'betslip.parallel_playing';
                        button = 'betslip.ok';
                    break;
                    case 'ERROR_CODE_PLAYER_NOT_AUTHENTICATED_DUE_TO_LUGAS_COOLING_PERIOD':
                        basicModal = true;
                        type = 'player_not_authenticated_due_to_lugas_cooling_period';
                        title = 'betslip.please_wait';
                        content = 'betslip.cool_down_period';
                        button = 'betslip.ok';
                        icon = 'clock-icon';
                    break;
                    case 'ERROR_CODE_PLAYER_NOT_AUTHENTICATED_DUE_TO_LUGAS_TECHNICAL_ISSUE':
                        basicModal = true;
                        type = 'player_not_authenticated_due_to_lugas_technical_issue';
                        title = 'betslip.bet_failed';
                        content = 'betslip.try_again';
                        button = 'betslip.ok';
                    break;
                    default:
                        break;
                }
            });

            if (selectionSuspended) {
                return selectionSuspended;
            }

            if (priceChanged) {
                return {
                    type: 'price_changed',
                    title: 'betslip.odds_have_changed',
                    bets: fullListOfErrors,
                    selections: selectionDetails,
                };
            }

            if (basicModal) {
                return {
                    type,
                    title,
                    content,
                    button,
                    icon,
                    translationVariables,
                    bets,
                    selectionDetails,
                };
            }

            return getGeneralModalDetails();
        }

        return null;
    },
);
